import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { getUserRole } from '../utils/userPermissions'
import apiClient from '../services/apiClient'
import {
  ArrowLeft,
  Save,
  X,
  User,
  CreditCard,
  Calendar,
  DollarSign,
  FileText,
  Mail,
  Hash
} from 'lucide-react'
import { invoiceService } from '../services/invoiceService'
import { adminManagementService, Admin } from '../services/adminManagementService'
import { userInvoiceService } from '../services/userInvoiceService'
import { Loading } from '../components/common/Loading'
import toast from 'react-hot-toast'
import { logger } from '../utils/logger'

interface SubscriptionPlan {
  id: number
  name: string
  price: number
  description: string
}

const InvoiceCreate: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useSelector((state: RootState) => state.auth)

  const userRole = getUserRole(user)
  const targetType = searchParams.get('target') // 'admin' ou 'client'

  // Estados principais
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Estados dos dados
  const [admins, setAdmins] = useState<Admin[]>([])
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([])
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)

  // Determinar tipo de fatura baseado no usuário
  const getInvoiceType = () => {
    if (userRole === 'super_admin') {
      return targetType === 'client' ? 'client' : 'admin'
    } else if (userRole === 'admin_with_subscription') {
      return 'client' // Admin com assinatura só pode criar faturas para clientes
    }
    return 'client'
  }

  const invoiceType = getInvoiceType()
  const [invoiceTypeState, setInvoiceTypeState] = useState<'subscription' | 'service'>('subscription')

  // Estados para busca de administrador
  const [adminSearchTerm, setAdminSearchTerm] = useState('')
  const [filteredAdmins, setFilteredAdmins] = useState<Admin[]>([])
  const [showAdminDropdown, setShowAdminDropdown] = useState(false)

  // Estados do formulário
  const [formData, setFormData] = useState({
    admin_id: '',
    target_id: '', // ID do cliente selecionado
    amount: '',
    description: '',
    due_date: '',
    payment_method: 'pix',
    type: 'issued',
    customer_name: '',
    customer_email: '',
    customer_document: '',
    subscription_plan_id: '',
    subscription_plan_name: ''
  })

  // Carregar dados iniciais
  useEffect(() => {
    if (invoiceType === 'admin') {
      loadAdmins()
      loadSubscriptionPlans()
    } else if (invoiceType === 'client') {
      // Para clientes, não precisamos carregar admins nem planos de assinatura
      // Apenas definir o tipo como 'service' para cobranças de serviços
      setInvoiceTypeState('service')
    }

    // Definir data de vencimento para 30 dias
    const dueDate = new Date()
    dueDate.setDate(dueDate.getDate() + 30)
    setFormData(prev => ({
      ...prev,
      due_date: dueDate.toISOString().split('T')[0]
    }))
  }, [invoiceType])

  // Filtrar administradores baseado no termo de busca
  useEffect(() => {
    if (adminSearchTerm.trim() === '') {
      setFilteredAdmins([])
      setShowAdminDropdown(false)
      return
    }

    const filtered = admins.filter(admin =>
      admin.name.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
      admin.id.toString().includes(adminSearchTerm)
    )

    setFilteredAdmins(filtered)
    setShowAdminDropdown(filtered.length > 0)
  }, [adminSearchTerm, admins])

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.admin-dropdown-container')) {
        setShowAdminDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const loadAdmins = async () => {
    try {
      setIsLoading(true)
      const response = await apiClient.get('/admin/with-subscriptions')
      setAdmins(response.data.data.admins || [])
    } catch (error) {
      logger.error('Erro ao carregar administradores:', error)
      toast.error('Erro ao carregar lista de administradores')
    } finally {
      setIsLoading(false)
    }
  }

  const loadSubscriptionPlans = async () => {
    try {
      const response = await apiClient.get('/subscription-plans/active')
      logger.debug('Planos carregados', { total: response.data?.data?.length || 0 })
      setSubscriptionPlans(response.data.data || [])
    } catch (error) {
      logger.error('Erro ao carregar planos de assinatura:', error)
      toast.error('Erro ao carregar planos de assinatura')
      setSubscriptionPlans([])
    }
  }

  const handleAdminSearch = (value: string) => {
    setAdminSearchTerm(value)
    if (value.trim() === '') {
      setSelectedAdmin(null)
      setFormData(prev => ({
        ...prev,
        admin_id: '',
        customer_name: '',
        customer_email: '',
        customer_document: ''
      }))
    }
  }

  const handleAdminSelect = async (admin: Admin) => {
    setSelectedAdmin(admin)
    setAdminSearchTerm(`${admin.name} (${admin.email})`)
    setShowAdminDropdown(false)

    try {
      // Buscar dados completos do admin com subscription
      const adminData = await adminManagementService.getAdminForInvoice(admin.id)
      
      // Preencher automaticamente os dados do cliente
      setFormData(prev => ({
        ...prev,
        admin_id: adminData.id.toString(),
        customer_name: adminData.name,
        customer_email: adminData.email,
        customer_document: adminData.document || '',
        // Se tiver subscription ativa, preencher o plano automaticamente
        subscription_plan_id: adminData.subscriptions?.[0]?.plan?.id?.toString() || '',
        subscription_plan_name: adminData.subscriptions?.[0]?.plan?.name || ''
      }))

      // Se tiver subscription ativa, definir automaticamente como tipo 'subscription'
      if (adminData.subscriptions?.[0]?.plan) {
        setInvoiceTypeState('subscription')
      }

    } catch (error) {
      logger.error('Erro ao buscar dados completos do admin:', error)
      // Fallback para dados básicos se houver erro
      setFormData(prev => ({
        ...prev,
        admin_id: admin.id.toString(),
        customer_name: admin.name,
        customer_email: admin.email,
        customer_document: ''
      }))
    }
  }

  const handleAdminClear = () => {
    setSelectedAdmin(null)
    setAdminSearchTerm('')
    setShowAdminDropdown(false)
    setFormData(prev => ({
      ...prev,
      admin_id: '',
      customer_name: '',
      customer_email: '',
      customer_document: ''
    }))
  }

  const handleInvoiceTypeChange = (type: 'subscription' | 'service') => {
    setInvoiceTypeState(type)

    if (type === 'subscription' && selectedAdmin?.subscriptions?.[0]?.plan) {
      const plan = selectedAdmin.subscriptions[0].plan;
      setFormData(prev => ({
        ...prev,
        amount: plan.price ? parseFloat(plan.price.toString()).toString() : '',
        description: `Assinatura ${plan.name}`,
        subscription_plan_id: plan.id.toString(),
        subscription_plan_name: plan.name
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        amount: '',
        description: '',
        subscription_plan_id: '',
        subscription_plan_name: ''
      }))
    }
  }

  const handlePlanChange = (planId: string) => {
    const plan = subscriptionPlans.find(p => p.id.toString() === planId)

    if (plan) {
      setFormData(prev => ({
        ...prev,
        subscription_plan_id: planId,
        subscription_plan_name: plan.name,
        amount: parseFloat(plan.price.toString()).toString(),
        description: `Assinatura ${plan.name}`
      }))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validação baseada no tipo de fatura
    const requiredFields = ['amount', 'description', 'due_date']
    
    // Para faturas de admin, admin_id é obrigatório
    if (invoiceType === 'admin' && !formData.admin_id) {
      requiredFields.push('admin_id')
    }

    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData])
    
    if (missingFields.length > 0) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }

    try {
      setIsSaving(true)

      const invoiceData = {
        ...formData,
        amount: parseFloat(formData.amount),
        admin_id: formData.admin_id ? parseInt(formData.admin_id) : undefined,
        client_id: formData.target_id ? parseInt(formData.target_id) : undefined, // ID do cliente selecionado
        payment_method: formData.payment_method as 'pix' | 'credit_card' | 'debit_card' | 'boleto' | 'bank_transfer',
        subscription_plan_id: formData.subscription_plan_id ? parseInt(formData.subscription_plan_id) : undefined,
        target_type: invoiceType, // 'admin' ou 'client'
        target_id: invoiceType === 'admin' ? (formData.admin_id ? parseInt(formData.admin_id) : undefined) : (formData.target_id ? parseInt(formData.target_id) : undefined)
      }

      // Usar serviço baseado no tipo de usuário
      if (userRole === 'attendant') {
        await userInvoiceService.createInvoice(invoiceData)
      } else {
        await invoiceService.createInvoice(invoiceData)
      }

      toast.success('Fatura criada com sucesso!')

      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/financial')
      }, 2000)

    } catch (error: any) {
      logger.error('Erro ao criar fatura:', error)
      toast.error(error.message || 'Erro ao criar fatura')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/financial')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Nova Fatura</h1>
              <p className="text-gray-600">
                {invoiceType === 'admin' && 'Criar fatura para administrador (plano de assinatura)'}
                {invoiceType === 'client' && 'Criar fatura para cliente (cobrança de serviços)'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Tipo de Fatura - apenas para super_admin */}
          {userRole === 'super_admin' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tipo de Fatura</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => handleInvoiceTypeChange('subscription')}
                className={`p-4 border-2 rounded-lg text-left transition-colors duration-200 ${
                  invoiceTypeState === 'subscription'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Assinatura</h4>
                    <p className="text-sm text-gray-500">Cobrança de plano de assinatura</p>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleInvoiceTypeChange('service')}
                className={`p-4 border-2 rounded-lg text-left transition-colors duration-200 ${
                  invoiceTypeState === 'service'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <h4 className="font-medium text-gray-900">Serviço</h4>
                    <p className="text-sm text-gray-500">Cobrança de outros serviços</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
          )}

          {/* Dados do Administrador - apenas para faturas de admin */}
          {invoiceType === 'admin' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dados do Administrador</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative admin-dropdown-container">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Administrador *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={adminSearchTerm}
                      onChange={(e) => handleAdminSearch(e.target.value)}
                      onFocus={() => setShowAdminDropdown(adminSearchTerm.trim() !== '' && filteredAdmins.length > 0)}
                      placeholder="Digite nome, email ou ID do administrador"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                    {selectedAdmin && (
                      <button
                        type="button"
                        onClick={handleAdminClear}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}

                    {/* Dropdown de administradores */}
                    {showAdminDropdown && filteredAdmins.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredAdmins.map(admin => (
                          <div
                            key={admin.id}
                            onClick={() => handleAdminSelect(admin)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <User className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{admin.name}</p>
                                <p className="text-sm text-gray-500">{admin.email}</p>
                                <p className="text-xs text-gray-400">ID: {admin.id}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              {invoiceTypeState === 'subscription' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plano de Assinatura *
                  </label>
                  <select
                    value={formData.subscription_plan_id}
                    onChange={(e) => handlePlanChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Selecione um plano</option>
                    {Array.isArray(subscriptionPlans) && subscriptionPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - R$ {parseFloat(plan.price.toString()).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Dados da Fatura */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados da Fatura</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor (R$) *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data de Vencimento *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="date"
                    name="due_date"
                    value={formData.due_date}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descreva o serviço ou produto"
                  required
                />
              </div>
            </div>
          </div>

          {/* Dados do Cliente */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dados do Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Cliente
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nome completo do cliente"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="customer_email"
                    value={formData.customer_email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CPF/CNPJ
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    name="customer_document"
                    value={formData.customer_document}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="000.000.000-00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Método de Pagamento
                </label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pix">PIX</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="boleto">Boleto Bancário</option>
                </select>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/financial')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Criando...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Criar Fatura</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  )
}

export default InvoiceCreate
